import React from "react";
import { createEmptyCareContext, normalizeCareContext } from "../../../utils/careContext";
import { createEmptySupportProfile, normalizeSupportProfile } from "../../../utils/supportProfile";
import { normalizeMedicationDetail } from "../shared/childMedicationHelpers";

export const EMPTY_PROFILE_DOCUMENTS = {
  medical: [],
  medications: [],
  behavioral: [],
};

export const createEmptyProfileFormState = () => ({
  name: "",
  age: "",
  dateOfBirth: "",
  photoURL: "",
  photoName: "",
  selectedConditions: [],
  foodAllergies: [],
  dietaryRestrictions: [],
  sensoryIssues: [],
  behavioralTriggers: [],
  communicationNeeds: [],
  careContext: createEmptyCareContext(),
  supportProfile: createEmptySupportProfile(),
  medicationDetails: [],
  uploadedDocuments: EMPTY_PROFILE_DOCUMENTS,
});

const normalizeSnapshotMedicationDetails = (medicationDetails = []) => (
  medicationDetails
    .map(normalizeMedicationDetail)
    .filter((entry) => entry.name || entry.id)
);

export const buildProfileSnapshotState = ({
  name = "",
  age = "",
  dateOfBirth = "",
  photoURL = "",
  photoName = "",
  selectedConditions = [],
  foodAllergies = [],
  dietaryRestrictions = [],
  sensoryIssues = [],
  behavioralTriggers = [],
  communicationNeeds = [],
  careContext = createEmptyCareContext(),
  supportProfile = createEmptySupportProfile(),
  medicationDetails = [],
  uploadedDocuments = EMPTY_PROFILE_DOCUMENTS,
} = {}) => ({
  snapshotName: name,
  snapshotAge: age,
  snapshotDateOfBirth: dateOfBirth,
  snapshotPhotoURL: photoURL || "",
  snapshotPhotoName: photoName || "",
  snapshotConditions: selectedConditions,
  snapshotFoodAllergies: foodAllergies,
  snapshotDietaryRestrictions: dietaryRestrictions,
  snapshotSensoryIssues: sensoryIssues,
  snapshotBehavioralTriggers: behavioralTriggers,
  snapshotCommunicationNeeds: communicationNeeds,
  snapshotCareContext: normalizeCareContext(careContext),
  snapshotSupportProfile: supportProfile,
  snapshotMedicationDetails: normalizeSnapshotMedicationDetails(medicationDetails),
  snapshotDocuments: uploadedDocuments || EMPTY_PROFILE_DOCUMENTS,
});

export const serializeProfileSnapshot = ({
  snapshotName = "",
  snapshotAge = "",
  snapshotDateOfBirth = "",
  snapshotPhotoURL = "",
  snapshotPhotoName = "",
  snapshotConditions = [],
  snapshotFoodAllergies = [],
  snapshotDietaryRestrictions = [],
  snapshotSensoryIssues = [],
  snapshotBehavioralTriggers = [],
  snapshotCommunicationNeeds = [],
  snapshotCareContext = createEmptyCareContext(),
  snapshotSupportProfile = createEmptySupportProfile(),
  snapshotMedicationDetails = [],
  snapshotDocuments = EMPTY_PROFILE_DOCUMENTS,
} = {}) => JSON.stringify({
  name: snapshotName,
  age: snapshotAge,
  dateOfBirth: snapshotDateOfBirth,
  photoURL: snapshotPhotoURL,
  photoName: snapshotPhotoName,
  conditions: snapshotConditions.map((item) => item?.code || item?.label || String(item)),
  foodAllergies: snapshotFoodAllergies,
  dietaryRestrictions: snapshotDietaryRestrictions,
  sensoryIssues: snapshotSensoryIssues,
  behavioralTriggers: snapshotBehavioralTriggers,
  communicationNeeds: snapshotCommunicationNeeds,
  careContext: normalizeCareContext(snapshotCareContext),
  supportProfile: normalizeSupportProfile(snapshotSupportProfile),
  medicationDetails: snapshotMedicationDetails.map((item) => ({
    id: item?.id || "",
    name: item?.name || "",
    category: item?.category || "",
    dosage: item?.dosage || "",
    scheduleType: item?.scheduleType || "",
    isArchived: Boolean(item?.isArchived),
  })),
  documents: {
    medical: (snapshotDocuments.medical || []).map((item) => item?.name || item?.url || ""),
    medications: (snapshotDocuments.medications || []).map((item) => item?.name || item?.url || ""),
    behavioral: (snapshotDocuments.behavioral || []).map((item) => item?.name || item?.url || ""),
  },
});

export const serializeProfileFormState = (formState = {}) => (
  serializeProfileSnapshot(buildProfileSnapshotState(formState))
);

export const useProfileSnapshotTracker = (formState = {}) => {
  const initialSnapshotRef = React.useRef(null);
  const formStateRef = React.useRef(formState);
  formStateRef.current = formState;

  const currentSnapshot = serializeProfileFormState(formState);

  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = currentSnapshot;
  }

  const hasUnsavedChanges = currentSnapshot !== initialSnapshotRef.current;

  React.useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const markCurrentSnapshot = React.useCallback((overrides = {}) => {
    initialSnapshotRef.current = serializeProfileFormState({
      ...formStateRef.current,
      ...overrides,
    });
  }, []);

  const resetSnapshot = React.useCallback((snapshot = "") => {
    initialSnapshotRef.current = snapshot;
  }, []);

  return {
    hasUnsavedChanges,
    markCurrentSnapshot,
    resetSnapshot,
  };
};
